/**
 * The bake's print WINDOW, as a pure function.
 *
 * Extracted verbatim from `MaskEditor`'s marker bake (WS-I / D-11) so `bake-mask-parity.test.ts`
 * exercises the SHIPPED chain rather than a re-implementation of it — the mistake WS-A had to
 * undo in the chroma branch, and the mistake this whole wave exists to repair one level up (two
 * "print area" implementations that had drifted 12.360 % apart).
 *
 * Every comment below travels with the code it explains: each one is the record of a field bug
 * (the "all grey" panel, the over-grown AI edge, the desynced feather), not decoration.
 */
import { featherMask, softAiEdgeRefine } from "./chroma.js";

/**
 * Gain applied to the print-area coverage plane before it drives anything.
 *
 * The oracle in `transparent/__fixtures__/keyer-parity.json` records this as `solidifyGain`, and
 * `bake-mask-parity.test.ts` asserts the two are the same number — so the pinned print-window
 * figures and the code can never drift apart.
 */
export const PRINT_SOLIDIFY_GAIN = 2;

export interface PrintWindowResult {
  mask: Uint8Array;
  usedAiEdge: boolean;
  aiEdgeSkipped: boolean;
}

/**
 * ×2 solidify (panel core → full so the grey despilled product cannot bleed over the design),
 * optional AI-edge refine with its ≥50 % safety guard, optional feather — in that order, which
 * is the order the bake has always run them in.
 */
export function solidifyPrintWindow(
  printAlpha: Uint8Array,
  width: number,
  height: number,
  opts: {
    gain?: number;
    feather?: number;
    aiEdge?: { matte: Uint8Array; keep: number } | null;
  },
): PrintWindowResult {
  const gain = opts.gain ?? PRINT_SOLIDIFY_GAIN;
  let usedAiEdge = false;
  let aiEdgeSkipped = false;
  // SOLIDIFY the print window before it drives the shell HOLE + the design CLIP. A panel
  // that keys only PARTIALLY (a non-uniform marker colour, or a tolerance that doesn't fully
  // cover it) would otherwise leave the despilled (GREY) product bleeding over the design —
  // so the print area "just goes grey" and swapping the test design barely changes it.
  // Boosting the coverage 4× makes any meaningfully-keyed pixel a FULL print pixel (design
  // fills solidly + shell holes fully there); only a thin true edge (raw < 64) keeps its soft
  // ramp. Raising the tolerance (watch the "Keyed %") closes any remainder on a varied panel.
  // Gain 2× (was 4×): solidify the strongly-keyed panel core (≥50% keyed → full) so the grey
  // product doesn't bleed, but DON'T over-grow into weak/partial edges — that was removing too
  // much (camera surround / edges keyed away, "too transparent, things hiding"). Lower the
  // marker Tolerance (or pick the panel colour) to show even more.
  let printSolid: Uint8Array = new Uint8Array(width * height);
  for (let i = 0; i < printSolid.length; i++) printSolid[i] = Math.min(255, printAlpha[i] * gain);
  // AI EDGE (opt-in): REFINE the marker print area with the AI matte — within the keyed
  // panel, PROTECT (cut out) pixels the matte strongly KEPT (a camera / logo / raised edge),
  // so the design doesn't cover them. It can only ever SHRINK the marker area, never invent
  // print outside it. `aiEdgeKeep` sets how strongly the matte must keep a pixel before it's
  // protected. Uses `softAiEdgeRefine` (chroma.ts): a smoothstep ramp just BELOW the protect
  // threshold plus a de-speckle pre-blur — so a soft/noisy BiRefNet matte gives smooth edge
  // transitions (intermediate mask values) instead of the old hard per-pixel threshold, which
  // let threshold-hovering pixels flip individually into salt-and-pepper grain + stair-steps.
  // SAFETY (unchanged): the matte often can't tell a flat print panel from the kept product,
  // so it sometimes keeps the WHOLE panel — which would erase the print area (the "all grey"
  // bug). If refining would remove more than HALF the keyed print area, the matte isn't
  // isolating small content: we SKIP AI edge, keep the reliable marker mask, and say so. The
  // >=128-based counts from softAiEdgeRefine keep this guard numerically identical.
  const ai = opts.aiEdge;
  if (ai && ai.matte.length === printSolid.length) {
    const { refined, baseCount, refinedCount } = softAiEdgeRefine(
      printSolid, ai.matte, width, height, { keep: ai.keep },
    );
    if (baseCount > 0 && refinedCount >= baseCount * 0.5) {
      printSolid.set(refined); // safe: removed a minority (real content), kept the panel
      usedAiEdge = true;
    } else {
      aiEdgeSkipped = true; // matte covered the print area → keep the marker mask
    }
  }
  // EDGE FEATHER (B3): soften the ×2-solidified panel edge AND the AI-edge boundary with a
  // ≤10px box blur BEFORE `printSolid` drives every downstream use — the shell HOLE, the
  // `printMask` handed to compose, and the quad fit — so all three stay consistent (feathering
  // only the shell would desync the design clip from the hole). A small blur can't move the
  // quad-detection coverage thresholds meaningfully; feather 0 is an identity (same reference).
  const feather = opts.feather ?? 0;
  if (feather > 0) {
    printSolid = featherMask(printSolid, width, height, feather);
  }
  return { mask: printSolid, usedAiEdge, aiEdgeSkipped };
}
